// @ts-strict-ignore
import { gql } from "@apollo/client";

import type { BrandFragment } from "@shared/data/fragments/BrandFragment/BrandFragment.fragment";
import type { VariantFragment } from "@shared/data/fragments/VariantFragment/VariantFragment.fragment";
import type {
  DailyPack,
  Product,
  WholesaleLineItem,
  WholesaleOrder,
  State,
} from "@shared/types/graphqlGenerated";

const WHOLESALE_CART = gql`
  fragment WholesaleCart on WholesaleOrder {
    id
    orderReceiptUrl
    estimatedTotal
    firstOrderPromotionPercentage
    isFirstOrderPromotionApplied
    itemTotal
    number
    savingsTotal
    poNumber
    isCreatedByPractitioner
    isDropship
    dropshipFee
    hasLabKit
    productSubtotal
    labKitSubtotal
    shippingAddress {
      id
      state
    }
    shippingTotal
    state
    taxTotal
    storePlatform {
      id
      name
    }
    lineItems {
      id
      amount
      promoTotal
      quantity
      variant {
        id
        sku
        availabilityStatus
        expectedReturnDate
        descriptor
        image
        imageNew
        isAllowedToShowcase
        isAvailableForPurchase
        name
        brandName
        wholesalePrice
        variantType
        emersonVariantId
        dailyPack {
          id
          nameWithSupply
          practitioner {
            id
            fullName
          }
        }
        brand {
          id
          name
        }
        product {
          id
          _legacyId
          heatSensitive
          isInStoreCategories
          name
          slug
          proposition65
          storeAllowedWholesale
          methodOfExposure
          prop65PrimaryChemical {
            id
            name
            regulation
          }
          prop65Risks {
            id
            name
            regulation
          }
          containsCbd
          nonShippableStates {
            id
            abbr
          }
        }
      }
    }
  }
`;

type WholesaleOrderSplinter = Pick<
  WholesaleOrder,
  | "id"
  | "estimatedTotal"
  | "firstOrderPromotionPercentage"
  | "isFirstOrderPromotionApplied"
  | "itemTotal"
  | "number"
  | "savingsTotal"
  | "shippingTotal"
  | "shippingAddress"
  | "state"
  | "taxTotal"
  | "storePlatform"
  | "poNumber"
  | "isCreatedByPractitioner"
  | "orderReceiptUrl"
  | "isDropship"
  | "dropshipFee"
  | "hasLabKit"
  | "productSubtotal"
  | "labKitSubtotal"
>;

type WholesaleLineItemSplinter = Pick<
  WholesaleLineItem,
  "id" | "amount" | "promoTotal" | "quantity"
>;

type VariantSplinter = Pick<
  VariantFragment,
  | "id"
  | "availabilityStatus"
  | "expectedReturnDate"
  | "descriptor"
  | "image"
  | "imageNew"
  | "isAllowedToShowcase"
  | "isAvailableForPurchase"
  | "msrp"
  | "name"
  | "brandName"
  | "sellPrice"
  | "sku"
  | "variantType"
  | "wholesalePrice"
  | "emersonVariantId"
>;

interface VariantWithInfo extends VariantSplinter {
  brand: Pick<BrandFragment, "id" | "name">;
  product: Pick<
    Product,
    | "id"
    | "_legacyId"
    | "heatSensitive"
    | "name"
    | "proposition65"
    | "methodOfExposure"
    | "storeAllowedWholesale"
    | "prop65PrimaryChemical"
    | "prop65Risks"
    | "slug"
    | "isInStoreCategories"
    | "kitTestInformation"
    | "containsCbd"
    | "nonShippableStates"
  > & {
    availableOrMasterVariant?: VariantSplinter;
    containsCbd?: boolean;
    nonShippableStates?: State[];
  };
  dailyPack?: Pick<DailyPack, "id" | "nameWithSupply"> & {
    practitioner?: Pick<DailyPack["practitioner"], "id" | "fullName">;
  };
}

interface LineItemWithVariant extends WholesaleLineItemSplinter {
  variant: VariantWithInfo;
}

interface WholesaleCart extends WholesaleOrderSplinter {
  lineItems: LineItemWithVariant[];
}

export { WHOLESALE_CART };
export type {
  LineItemWithVariant as WholesaleLineItem,
  VariantWithInfo as WholesaleLineItemVariant,
  WholesaleCart,
};
